import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { initSentry } from './lib/sentry';

// Init Sentry AVANT tout autre code — pour capturer les erreurs des
// modules importes apres (App, hooks, etc.). RGPD : voir lib/sentry.ts
// (anonymise, pas d'email).
initSentry();

// Capture GLOBALE du code parrainage depuis l'URL au boot. Avant : capture
// uniquement dans SellerShopWrapper, donc si le visiteur arrivait sur
// /register directement (ou ailleurs que /seller/X) le ref etait perdu.
// Resultat : 33 parrainages crees en BDD, 0 conversion enregistree.
// On lit ?ref= ET (backward compat) le hash #/...?ref= pour les vieux
// liens partages avant la migration BrowserRouter.
try {
  let ref: string | null = null;
  const search = new URLSearchParams(window.location.search);
  ref = search.get('ref');
  if (!ref && window.location.hash.includes('?')) {
    const hashQuery = window.location.hash.split('?')[1];
    ref = new URLSearchParams(hashQuery).get('ref');
  }
  if (ref && ref.trim()) {
    const code = ref.toUpperCase().trim();
    localStorage.setItem('glowp_referral_code', code);
    // Track clic sur lien parrainage (fire-and-forget, ne bloque pas le boot).
    // Permet a l'admin de voir le funnel : clics -> inscriptions -> conversions.
    // Endpoint POST /api/v1/analytics/event (whitelist 'referral_link_clicked').
    try {
      const apiBase =
        (import.meta.env.VITE_API_URL as string | undefined) ??
        'https://glowp-production.up.railway.app';
      fetch(`${apiBase}/api/v1/analytics/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          name: 'referral_link_clicked',
          properties: {
            code,
            landing_path: window.location.pathname,
          },
          page: window.location.pathname,
        }),
      }).catch(() => {
        /* silent */
      });
    } catch {
      /* silent */
    }
  }
} catch {
  // localStorage Safari private mode etc. — silencieux
}

import App from './App';
import './index.css';
import './i18n';
import { initUserActivityTracker } from './lib/userActivityTracker';
import { recordVisit } from './lib/streak';
import { UPDATE_AVAILABLE_EVENT } from './lib/lazyWithRetry';
import AppUpdateNotice from './components/AppUpdateNotice';
import AchievementToast from './components/AchievementToast';
import { SkeletonStyles } from './components/common/Skeleton';

// Track user real interactions (mousemove/click/scroll/keydown) so that the
// API client can signal them to the backend via the X-User-Active header.
// Without this, last_activity_at was bumped on every poll, making any open
// tab appear "active now" in the admin live-users view.
initUserActivityTracker();

// Streak tracker : enregistre 1 visite par jour. Si l'user est venu hier
// aussi → streak++, sinon reset à 1. Idempotent au sein d'une journée.
try {
  const streak = recordVisit();
  // Trigger feedback FREQUENT_VISIT au 5e jour consecutif (user engage,
  // taux de reponse honnete eleve). Differe pour ne pas perturber le boot.
  if (streak.current >= 5 && streak.justIncremented) {
    import('./components/feedback/AppFeedbackPrompt').then(
      ({ hasBeenPrompted, triggerAppFeedback }) => {
        if (!hasBeenPrompted('FREQUENT_VISIT')) {
          setTimeout(() => triggerAppFeedback('FREQUENT_VISIT'), 5000);
        }
      }
    );
  }
} catch {
  /* never block the app on streak */
}

// iOS: status bar ne doit pas overlapper le contenu
if (Capacitor.isNativePlatform()) {
  StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
  StatusBar.setStyle({ style: Style.Light }).catch(() => {});
  StatusBar.setBackgroundColor({ color: '#F5EFE6' }).catch(() => {});
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// PWA : enregistrer le service worker avec notification de mise à jour.
// Avant : reload silencieux brutal qui faisait perdre la saisie en cours.
// Maintenant : on émet le même event que lazyWithRetry, capté par
// <AppUpdateNotice /> qui propose une banner Glowp avec bouton "Recharger".
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then(reg => {
        // Verifier les mises a jour toutes les 5 minutes
        setInterval(() => reg.update(), 5 * 60 * 1000);
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (
                newWorker.state === 'activated' &&
                navigator.serviceWorker.controller
              ) {
                window.dispatchEvent(new CustomEvent(UPDATE_AVAILABLE_EVENT));
              }
            });
          }
        });
      })
      .catch(() => {});
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        {/* Banner monté hors Suspense : reste visible même si une route
            lazy est en attente de chunk. */}
        <AppUpdateNotice />
        {/* Toast achievements (1ère annonce, 1er favori…) — déclenché
            depuis n'importe où via triggerAchievement(). */}
        <AchievementToast />
        {/* Injecte les keyframes shimmer du skeleton (une seule fois). */}
        <SkeletonStyles />
        <Suspense
          fallback={
            <div style={{ padding: 40, textAlign: 'center' }}>
              Chargement...
            </div>
          }
        >
          <App />
        </Suspense>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>
);
